/**
 * File Type Utilities for FormCrafter
 * Functions for validating and filtering file types against system-allowed types
 */

import {
  EXTENSION_TO_MIME,
  IMAGE_EXTENSIONS,
  DEFAULT_IMAGE_ACCEPT,
  DEFAULT_ATTACHMENT_ACCEPT,
} from '../../constants/formCrafter.constant';

/**
 * Normalizes an extension to lowercase with leading dot
 * @param {string} ext - Extension string (e.g., "jpg", ".JPG", "PNG")
 * @returns {string} - Normalized extension (e.g., ".jpg", ".png")
 */
const normalizeExtension = (ext) => {
  if (!ext || typeof ext !== 'string') return '';
  const trimmed = ext.trim().toLowerCase();
  return trimmed.startsWith('.') ? trimmed : `.${trimmed}`;
};

/**
 * Checks if an extension is allowed by the system
 * @param {string} ext - Extension to check
 * @returns {boolean} - True if extension is in EXTENSION_TO_MIME
 */
export const isSystemAllowedExtension = (ext) => {
  const normalized = normalizeExtension(ext);
  return normalized in EXTENSION_TO_MIME;
};

/**
 * Checks if an extension is a valid image extension
 * @param {string} ext - Extension to check
 * @returns {boolean} - True if extension is an image type
 */
export const isImageExtension = (ext) => {
  const normalized = normalizeExtension(ext);
  return IMAGE_EXTENSIONS.includes(normalized);
};

/**
 * Parses a comma-separated accept string into an array of extensions
 * @param {string} acceptString - Accept string (e.g., ".jpg,.png,csv")
 * @returns {string[]} - Array of normalized extensions
 */
const parseAcceptString = (acceptString) => {
  if (!acceptString || typeof acceptString !== 'string') return [];
  return acceptString
    .split(',')
    .map((ext) => normalizeExtension(ext))
    .filter((ext) => ext.length > 1); // Filter out empty or just "."
};

/**
 * Filters admin-configured extensions to only include system-allowed types
 * For image fields: Only allows image extensions
 * For file/attachment fields: Allows all system-allowed extensions
 *
 * @param {string} configuredAccept - Admin-configured accept string
 * @param {string} fieldType - Field type ('image', 'file', 'attachment')
 * @returns {string} - Filtered accept string with only valid extensions
 */
export const getValidatedAcceptString = (configuredAccept, fieldType) => {
  const isImageField = fieldType === 'image';

  // If no custom config, return defaults
  if (!configuredAccept || configuredAccept === '*') {
    return isImageField ? DEFAULT_IMAGE_ACCEPT : DEFAULT_ATTACHMENT_ACCEPT;
  }

  // Parse configured extensions
  const configuredExtensions = parseAcceptString(configuredAccept);

  // Filter to valid extensions based on field type
  const validExtensions = configuredExtensions.filter((ext) => {
    // Must be system-allowed
    if (!isSystemAllowedExtension(ext)) return false;
    // For image fields, must also be an image extension
    if (isImageField && !isImageExtension(ext)) return false;
    return true;
  });

  // If no valid extensions remain, return defaults
  if (validExtensions.length === 0) {
    return isImageField ? DEFAULT_IMAGE_ACCEPT : DEFAULT_ATTACHMENT_ACCEPT;
  }

  return validExtensions.join(',');
};

/**
 * Gets the file configuration with validated accept types
 * @param {Object} field - Field definition
 * @param {Object} defaultConfig - Default configuration for the field type
 * @returns {Object} - Configuration with validated accept string
 */
export const getValidatedFileConfig = (field, defaultConfig) => {
  const { type, accept: customAccept, maxItems, maxSize } = field;

  // Get validated accept string
  const accept = getValidatedAcceptString(customAccept, type);

  // Use custom value if it's a positive number, otherwise use defaults
  // This handles undefined, null, 0, and empty string cases
  const effectiveMaxItems =
    typeof maxItems === 'number' && maxItems > 0
      ? maxItems
      : defaultConfig?.maxItems || 1;

  const effectiveMaxSize =
    typeof maxSize === 'number' && maxSize > 0
      ? maxSize
      : defaultConfig?.maxSize || 10;

  return {
    accept,
    maxItems: effectiveMaxItems,
    maxSize: effectiveMaxSize,
  };
};
